import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  AfterViewInit,
  ChangeDetectorRef,
  booleanAttribute,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslocoModule],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
  private _value: any = '';
  private _touched = false;
  showClear = false;

  constructor(private cdr: ChangeDetectorRef) {}
  @Input() type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'search' | 'date' = 'text';
  @Input() placeholder = '';
  @Input() label = '';
  @Input() id = '';
  @Input() name = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() minLength: number | null = null;
  @Input() maxLength: number | null = null;
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() pattern: string | null = null;
  @Input() autocomplete = 'off';
  @Input() errorMessage = '';
  @Input() hint = '';
  @Input() hasIcon = false;
  @Input() iconPosition: 'left' | 'right' = 'left';

  @Input({ transform: booleanAttribute }) showClearButton = false;

  // Track touched state
  @Output() valueChange = new EventEmitter<string>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() focus = new EventEmitter<FocusEvent>();
  @Output() clear = new EventEmitter<void>();

  // Internal state
  onChange = (_: any) => {};
  onTouched = () => {};

  get inputId(): string {
    return this.id || this.name;
  }

  get hasError(): boolean {
    // Show error only if field is touched AND errorMessage is provided
    return this.errorMessage !== '' && this._touched;
  }

  get errorMsg(): string {
    return this.hasError ? this.errorMessage : '';
  }

  get value(): any {
    return this._value;
  }

  set value(val: any) {
    this._value = val;
    this.onChange(val);
    this.updateShowClear();
  }

  writeValue(value: any): void {
    this._value = value || '';
    this.updateShowClear();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange(event: any): void {
    const value = event.target.value;
    this.value = value;
    this.valueChange.emit(value);
  }

  onBlur(): void {
    this._touched = true;
    this.onTouched();
  }

  /**
   * Manually mark this input as touched (can be called externally)
   */
  markAsTouched(): void {
    this._touched = true;
  }

  onInputBlur(event: FocusEvent): void {
    this.onBlur();
    this.blur.emit(event);
  }

  onInputFocus(event: FocusEvent): void {
    this.focus.emit(event);
  }

  onClear(): void {
    this.value = '';
    this.valueChange.emit('');
    this.clear.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showClearButton']) {
      this.updateShowClear();
    }
  }

  ngAfterViewInit(): void {
    // Recalculate after view init to ensure state is correct after hydration
    this.updateShowClear();
  }

  updateShowClear(): void {
    const newValue = !!(this.showClearButton && this._value && this._value.length > 0);
    if (this.showClear !== newValue) {
      this.showClear = newValue;
    }
  }
}
